import { useCallback, useRef } from 'react';

type SendFunction = (value: any) => Promise<void>;

export const useThrottledSend = (send: SendFunction, minInterval: number) => {
    const lastSendTime = useRef<number>(0);
    const pendingValue = useRef<any | null>(null);
    const sendPromise = useRef<Promise<void> | null>(null);
    const timeoutId = useRef<NodeJS.Timeout | null>(null);
    const hasBeenCalled = useRef<boolean>(false);

    const throttledSend = useCallback(async (value: any) => {
        hasBeenCalled.current = true;
        pendingValue.current = value;

        // If there's an ongoing send, just update the pending value and wait for it to finish
        if (sendPromise.current) {
            await sendPromise.current;
            return;
        }

        const now = Date.now();
        const timeSinceLastSend = now - lastSendTime.current;
        
        // Send immediately if first time or enough time has passed
        if (lastSendTime.current === 0 || timeSinceLastSend >= minInterval) {
            const valueToSend = pendingValue.current;
            pendingValue.current = null;
            
            sendPromise.current = send(valueToSend);
            try {
                lastSendTime.current = Date.now();
                await sendPromise.current;
            } finally {
                sendPromise.current = null;
            }
        } else {
            // Schedule throttled send
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }

            const delay = minInterval - timeSinceLastSend;
            timeoutId.current = setTimeout(async () => {
                if (pendingValue.current === null) return;

                const valueToSend = pendingValue.current;
                pendingValue.current = null;

                sendPromise.current = send(valueToSend);
                try {
                    await sendPromise.current;
                    lastSendTime.current = Date.now();
                } finally {
                    sendPromise.current = null;
                }
            }, delay);
        }
    }, [send, minInterval]);

    return throttledSend;
}; 